/**
 * 蓝牙电子秤 Android 原生桥接适配层
 * 通过 Android WebView JavascriptInterface 调用原生蓝牙功能
 * 提供与 bluetooth-scale-core.ts 相同的接口
 */

import i18n from '../../../i18n';

// Android 原生回调接口声明
declare global {
  interface Window {
    // Android 原生回调接口
    __onBluetoothDeviceFound?: (name: string, address: string) => void;
    __onBluetoothConnectionChanged?: (connected: boolean, deviceName: string) => void;
    __onBluetoothWeightData?: (weight: number, unit: string, rawData: string) => void;
    __onBluetoothError?: (error: string) => void;
    __onBluetoothPermissionsResult?: (granted: boolean) => void;
  }
}

// 类型定义（保持与 bluetooth-scale-core.ts 一致）
export interface WeightData {
  weight: number;
  unit: string;
  rawData: string;
  timestamp: Date;
}

export interface ErrorData {
  message: string;
  error: any;
  timestamp: Date;
}

export interface DeviceInfo {
  deviceId?: string;
  deviceName?: string;
  serviceId?: string;
  characteristicId?: string;
  isHC02Model?: boolean;
}

export interface BluetoothScaleDevice {
  deviceId?: string;
  name?: string;
  connectable?: boolean;
  address?: string; // Android 设备地址
}

export interface ConnectionResult {
  success: boolean;
  deviceInfo?: DeviceInfo;
  error?: string;
}

/**
 * Android 原生蓝牙适配器
 * 通过 JavascriptInterface 调用 Android 原生蓝牙功能
 */
export class BluetoothScaleAndroidBridge {
  private isConnected: boolean = false;
  private deviceInfo: DeviceInfo | null = null;
  private weightCallback: ((data: WeightData) => void) | null = null;
  private errorCallback: ((data: ErrorData) => void) | null = null;
  private discoveredDevices: BluetoothScaleDevice[] = [];
  private connectionResolve: ((result: ConnectionResult) => void) | null = null;
  private permissionResolve: ((granted: boolean) => void) | null = null;

  constructor() {
    this.setupAndroidCallbacks();
  }

  /**
   * 设置 Android 原生回调
   */
  private setupAndroidCallbacks() {
    // 设备发现回调
    window.__onBluetoothDeviceFound = (name: string, address: string) => {
      const device: BluetoothScaleDevice = {
        name,
        address,
        deviceId: address,
        connectable: true
      };
      this.discoveredDevices.push(device);
    };

    // 连接状态变化回调
    window.__onBluetoothConnectionChanged = (connected: boolean, deviceName: string) => {
      this.isConnected = connected;

      if (connected) {
        this.deviceInfo = {
          deviceName: deviceName || String(i18n.t('pda.pages.bluetoothScale.unknownDevice')),
          deviceId: deviceName
        };

        if (this.connectionResolve) {
          this.connectionResolve({
            success: true,
            deviceInfo: this.deviceInfo
          });
          this.connectionResolve = null;
        }
      } else {
        this.deviceInfo = null;

        // 断开连接时，清空重量数据并通知前端
        if (this.weightCallback) {
          this.weightCallback({
            weight: 0,
            unit: '',
            rawData: '',
            timestamp: new Date()
          });
        }

        if (this.connectionResolve) {
          this.connectionResolve({
            success: false,
            error: '连接已断开'
          });
          this.connectionResolve = null;
        }
      }
    };

    // 重量数据回调
    window.__onBluetoothWeightData = (weight: number, unit: string, rawData: string) => {
      if (this.weightCallback) {
        this.weightCallback({
          weight,
          unit,
          rawData,
          timestamp: new Date()
        });
      } else {
      }
    };

    // 错误回调
    window.__onBluetoothError = (error: string) => {

      if (this.errorCallback) {
        this.errorCallback({
          message: error,
          error: new Error(error),
          timestamp: new Date()
        });
      }

      if (this.connectionResolve) {
        this.connectionResolve({
          success: false,
          error
        });
        this.connectionResolve = null;
      }
    };

    // 权限请求结果回调
    window.__onBluetoothPermissionsResult = (granted: boolean) => {
      if (this.permissionResolve) {
        this.permissionResolve(granted);
        this.permissionResolve = null;
      }
    };
  }

  /**
   * 检查是否在 Android WebView 环境中
   */
  static isAndroidEnvironment(): boolean {
    return typeof window.PdaApp !== 'undefined' && typeof window.PdaApp.isBluetoothScaleAvailable !== 'undefined';
  }

  /**
   * 初始化蓝牙适配器
   */
  async initBluetooth(): Promise<boolean> {
    try {
      if (!window.PdaApp) {
        throw new Error('Android 原生蓝牙接口不可用');
      }

      const available = window.PdaApp?.isBluetoothAvailable?.();
      if (!available) {
        throw new Error('蓝牙不可用，请检查蓝牙是否开启');
      }

      const hasPermissions = window.PdaApp?.hasBluetoothPermissions?.();
      if (!hasPermissions) {
        // 请求权限
        const granted = await this.requestPermissions();
        if (!granted) {
          throw new Error('蓝牙权限未授予');
        }
      }

      return true;
    } catch (error) {
      this.handleError('蓝牙初始化失败', error);
      return false;
    }
  }

  /**
   * 请求蓝牙权限
   */
  private requestPermissions(): Promise<boolean> {
    return new Promise((resolve) => {
      this.permissionResolve = resolve;
      window.PdaApp?.requestBluetoothPermissions?.();

      // 设置超时
      setTimeout(() => {
        if (this.permissionResolve) {
          this.permissionResolve(false);
          this.permissionResolve = null;
        }
      }, 30000);
    });
  }

  /**
   * 搜索蓝牙设备
   */
  async requestDevice(): Promise<BluetoothScaleDevice | null> {
    try {
      if (!window.PdaApp) {
        throw new Error('Android 原生蓝牙接口不可用');
      }

      this.discoveredDevices = [];
      window.PdaApp?.startBluetoothScan?.();

      // 等待扫描完成（10秒）
      await new Promise(resolve => setTimeout(resolve, 10000));

      // 停止扫描
      window.PdaApp?.stopBluetoothScan?.();

      if (this.discoveredDevices.length === 0) {
        return null;
      }

      // 返回第一个设备（实际使用时应该让用户选择）
      return this.discoveredDevices[0];
    } catch (error) {
      this.handleError('搜索设备失败', error);
      return null;
    }
  }

  /**
   * 停止搜索设备
   */
  stopScan(): void {
    window.PdaApp?.stopBluetoothScan?.();
  }

  /**
   * 连接蓝牙设备
   */
  async connectDevice(address?: string): Promise<ConnectionResult> {
    try {
      if (!window.PdaApp) {
        throw new Error('Android 原生蓝牙接口不可用');
      }

      // 如果没有提供地址，先搜索设备
      if (!address) {
        this.discoveredDevices = [];
        window.PdaApp?.startBluetoothScan?.();

        // 等待扫描完成
        await new Promise(resolve => setTimeout(resolve, 10000));
        window.PdaApp?.stopBluetoothScan?.();

        if (this.discoveredDevices.length === 0) {
          return {
            success: false,
            error: '未找到蓝牙设备'
          };
        }

        // 使用第一个发现的设备
        address = this.discoveredDevices[0].address;
      }

      if (!address) {
        return {
          success: false,
          error: '无效的设备地址'
        };
      }

      // 创建 Promise 等待连接结果
      const connectionPromise = new Promise<ConnectionResult>((resolve) => {
        this.connectionResolve = resolve;

        // 设置超时
        setTimeout(() => {
          if (this.connectionResolve) {
            this.connectionResolve({
              success: false,
              error: '连接超时'
            });
            this.connectionResolve = null;
          }
        }, 30000);
      });

      // 发起连接
      window.PdaApp?.connectDevice?.(address);

      return await connectionPromise;
    } catch (error) {
      this.handleError('连接设备失败', error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * 设置重量数据回调
   */
  onWeightChange(callback: (data: WeightData) => void): void {
    this.weightCallback = callback;
  }

  /**
   * 设置错误回调
   */
  onError(callback: (data: ErrorData) => void): void {
    this.errorCallback = callback;
  }

  /**
   * 断开连接
   */
  async disconnect(): Promise<void> {
    try {
      window.PdaApp?.disconnect?.();
      this.isConnected = false;
      this.deviceInfo = null;
      this.discoveredDevices = [];
    } catch (error) {
    }
  }

  /**
   * 关闭蓝牙适配器
   */
  async closeBluetooth(): Promise<void> {
    await this.disconnect();
  }

  /**
   * 获取已发现的设备列表
   */
  getDiscoveredDevices(): BluetoothScaleDevice[] {
    try {
      const json = window.PdaApp?.getDiscoveredDevices?.();
      if (json) {
        const devices = JSON.parse(json);
        return devices.map((d: any) => ({
          name: d.name,
          address: d.address,
          deviceId: d.address,
          connectable: true
        }));
      }
    } catch (error) {
    }
    return this.discoveredDevices;
  }

  /**
   * 错误处理
   */
  private handleError(message: string, error: any): void {
    if (this.errorCallback) {
      this.errorCallback({
        message,
        error,
        timestamp: new Date()
      });
    }
  }
}

// 导出模块
export default BluetoothScaleAndroidBridge;

